import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { OrderSchemaType, ItemPath } from '../../../../order.schema';
import ApiService from '../../../../../../../../services/ApiService';
import { FeedbackContext } from '../../../../../../../../contexts/FeedbackContext';
import { FormGroup, Input, Label, Select } from '../../../../style';
import { CalcHint, CalculatedValue, DimensionsGrid, FullRowFormGroup } from './FinalProductDimensionsSection.style';

interface FinalProductDimensionsSectionProps {
  itemPath: ItemPath;
}

type DimColumn = 'diameter' | 'thickness' | 'width';

interface FormatDimensionRule {
  dim1: DimColumn;
  dim2?: DimColumn;
}

const FORMAT_DIMENSIONS: Record<string, FormatDimensionRule> = {
  redondo: { dim1: 'diameter' },
  sextavado: { dim1: 'diameter' },
  quadrado: { dim1: 'thickness', dim2: 'width' },
  chapa: { dim1: 'thickness', dim2: 'width' },
  tubo: { dim1: 'thickness', dim2: 'diameter' },
  anel: { dim1: 'thickness', dim2: 'diameter' },
  tubular: { dim1: 'thickness', dim2: 'diameter' },
};

interface ReferenceMaterial {
  materialId: number;
  materialName: string;
  density: number;
}

const round4 = (value: number) => Math.round(value * 10000) / 10000;

const materialDensityCache = new Map<number, ReferenceMaterial | null>();

const fetchMaterialDensity = async (materialId: number): Promise<ReferenceMaterial | null> => {
  const cached = materialDensityCache.get(materialId);
  if (cached !== undefined) return cached;

  const res = await ApiService.steelOrders.getMaterials({ id: materialId });
  const raw = Array.isArray(res.data) ? res.data[0] : res.data;
  const density = Number(raw?.density);
  const resolved: ReferenceMaterial | null = raw && !isNaN(density) && density > 0
    ? { materialId, materialName: raw.name || '', density }
    : null;
  materialDensityCache.set(materialId, resolved);
  return resolved;
};

const FinalProductDimensionsSection: React.FC<FinalProductDimensionsSectionProps> = ({ itemPath }) => {
  const { control, setValue, getValues } = useFormContext<OrderSchemaType>();
  const { setFeedback } = useContext(FeedbackContext);
  const isTopLevelItem = !itemPath.includes('.subItems.');

  const notifyError = useCallback((message: string) => {
    setFeedback({ message, isOpen: true, statusOk: false });
  }, [setFeedback]);

  const [formats, setFormats] = useState<{ id: number; name: string }[]>([]);
  const [materials, setMaterials] = useState<ReferenceMaterial[]>([]);
  const [selectedMaterialId, setSelectedMaterialId] = useState<number | undefined>(undefined);
  const [calculatedMass, setCalculatedMass] = useState<number | null>(null);

  const formatId = useWatch({ control, name: `${itemPath}.formatId` as any }) as number | undefined;
  const scriptId = useWatch({ control, name: `${itemPath}.scriptId` as any }) as number | undefined;
  const lengthValue = useWatch({ control, name: `${itemPath}.length` as any }) as number | string | undefined;
  const weightValue = useWatch({ control, name: `${itemPath}.weight` as any }) as number | string | undefined;
  const diameterValue = useWatch({ control, name: `${itemPath}.diameter` as any }) as number | string | undefined;
  const thicknessValue = useWatch({ control, name: `${itemPath}.thickness` as any }) as number | string | undefined;
  const widthValue = useWatch({ control, name: `${itemPath}.width` as any }) as number | string | undefined;
  const dimValues: Record<DimColumn, number | string | undefined> = {
    diameter: diameterValue,
    thickness: thicknessValue,
    width: widthValue,
  };

  const subItemsWatch = useWatch({ control, name: `${itemPath}.subItems` as any }) as any[] | undefined;
  const subItemMaterialIdsKey = (subItemsWatch || [])
    .map(subItem => Number(subItem?.materialId))
    .filter(id => !!id && !isNaN(id))
    .join(',');
  const subItemMaterialIds = useMemo(
    () => (subItemMaterialIdsKey ? subItemMaterialIdsKey.split(',').map(Number) : []),
    [subItemMaterialIdsKey],
  );

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const weightInitedRef = useRef(false);
  const calcFailedRef = useRef(false);

  const unitName = useWatch({ control, name: `${itemPath}.unitOfMeasurementName` as any }) as string | undefined;
  const isKgUnit = String(unitName || '').toUpperCase() === 'KG';

  useEffect(() => {
    if (!isTopLevelItem) return;
    let active = true;
    ApiService.stockLookup.getFormats()
      .then(res => { if (active) setFormats(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (active) notifyError('Não foi possível carregar os formatos. O cálculo de massa fica indisponível.'); });
    return () => { active = false; };
  }, [isTopLevelItem, notifyError]);

  useEffect(() => {
    if (!isTopLevelItem) return;
    let active = true;

    const applyMaterials = (mats: ReferenceMaterial[]) => {
      if (!active) return;
      setMaterials(mats);
      setSelectedMaterialId(mats[0]?.materialId);
    };

    if (scriptId) {
      const quantity = Number(getValues(`${itemPath}.quantity` as any)) || 1;
      ApiService.steelOrders.getScriptBreakdown(Number(scriptId), { quantity })
        .then(res => {
          const mats: ReferenceMaterial[] = (Array.isArray(res.data?.materials) ? res.data.materials : [])
            .filter((m: any) => m?.density !== undefined && m?.density !== null)
            .map((m: any) => ({
              materialId: Number(m.materialId),
              materialName: m.materialName || '',
              density: Number(m.density),
            }));
          applyMaterials(mats);
        })
        .catch(() => { if (active) notifyError('Não foi possível carregar a densidade do roteiro. A massa não será calculada.'); });
      return () => { active = false; };
    }

    const materialIds = (subItemMaterialIds || []).filter(Boolean);
    if (materialIds.length === 0) {
      applyMaterials([]);
      return () => { active = false; };
    }

    Promise.all(materialIds.map(id => fetchMaterialDensity(id)))
      .then(results => applyMaterials(results.filter(Boolean) as ReferenceMaterial[]))
      .catch(() => { if (active) notifyError('Não foi possível carregar a densidade da matéria-prima. A massa não será calculada.'); });

    return () => { active = false; };
  }, [scriptId, isTopLevelItem, itemPath, getValues, subItemMaterialIds, notifyError]);

  const resolveFormatType = useCallback((): string => {
    const fid = Number(formatId);
    if (!fid) return '';
    const match = formats.find(f => Number(f.id) === fid);
    return match ? String(match.name || '').toLowerCase().trim() : '';
  }, [formatId, formats]);

  const resolveDensity = useCallback((): number | null => {
    if (materials.length === 0) return null;
    if (materials.length === 1) return materials[0].density;
    const selected = materials.find(m => m.materialId === selectedMaterialId) || materials[0];
    return selected.density;
  }, [materials, selectedMaterialId]);

  const runCalc = useCallback(async (source: 'length' | 'weight') => {
    const formatType = resolveFormatType();
    const rule = FORMAT_DIMENSIONS[formatType];
    if (!rule) return;
    const density = resolveDensity();
    if (!density) return;

    const dim1 = Number(getValues(`${itemPath}.${rule.dim1}` as any)) || 0;
    const dim2 = rule.dim2 ? (Number(getValues(`${itemPath}.${rule.dim2}` as any)) || 0) : 0;

    try {
      if (source === 'length') {
        const length = Number(getValues(`${itemPath}.length` as any));
        if (!length || !isFinite(length)) return;
        const res = await ApiService.pcp.calculateByFormat({ formatType, dim1, dim2, density, quantity: length, unit: 'MM' });
        const weight = res.data?.totalWeight;
        if (typeof weight === 'number' && isFinite(weight)) {
          calcFailedRef.current = false;
          setCalculatedMass(round4(weight));
        }
      } else {
        const weight = Number(getValues(`${itemPath}.weight` as any));
        if (!weight || !isFinite(weight)) return;
        const res = await ApiService.pcp.calculateByFormat({ formatType, dim1, dim2, density, quantity: weight, unit: 'KG' });
        const length = res.data?.lengthInMm;
        if (typeof length === 'number' && isFinite(length)) {
          calcFailedRef.current = false;
          setCalculatedMass(round4(weight));
          setValue(`${itemPath}.length` as any, round4(length), { shouldDirty: true, shouldValidate: false });
        }
      }
    } catch {
      setCalculatedMass(null);
      if (calcFailedRef.current) return;
      calcFailedRef.current = true;
      notifyError('Não foi possível calcular a massa. Confira se o formato cadastrado é reconhecido.');
    }
  }, [resolveFormatType, resolveDensity, getValues, setValue, itemPath, notifyError]);

  const scheduleCalc = useCallback((source: 'length' | 'weight') => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { runCalc(source); }, 500);
  }, [runCalc]);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  useEffect(() => {
    if (!isTopLevelItem) return;
    if (!FORMAT_DIMENSIONS[resolveFormatType()]) return;
    if (!resolveDensity()) return;
    const length = Number(getValues(`${itemPath}.length` as any));
    if (!length || !isFinite(length)) return;
    scheduleCalc('length');
  }, [
    isTopLevelItem,
    resolveFormatType,
    resolveDensity,
    getValues,
    itemPath,
    scheduleCalc,
    diameterValue,
    thicknessValue,
    widthValue,
    lengthValue,
  ]);

  // Em KG o peso é espelho da quantidade — não dirige o comprimento.
  useEffect(() => {
    if (!weightInitedRef.current) {
      weightInitedRef.current = true;
      return;
    }
    if (isKgUnit) return;
    if (weightValue === undefined || weightValue === null || weightValue === '') return;
    scheduleCalc('weight');
    // eslint-disable-next-line
  }, [weightValue, isKgUnit]);

  const formatType = resolveFormatType();
  const rule = FORMAT_DIMENSIONS[formatType];
  const activeDensity = resolveDensity();

  const displayMass = calculatedMass;

  if (!isTopLevelItem || !formatId || !rule) return null;

  return (
    <DimensionsGrid columns={3}>
      <FormGroup>
        <Label htmlFor={`${itemPath}.${rule.dim1}`}>Dimensão 1 (mm)</Label>
        <Input
          id={`${itemPath}.${rule.dim1}`}
          autoComplete="off"
          type="number"
          step="any"
          value={(dimValues[rule.dim1] as any) ?? ''}
          onChange={(e) => setValue(`${itemPath}.${rule.dim1}` as any, e.target.value === '' ? undefined : Number(e.target.value), { shouldDirty: true, shouldValidate: false })}
          placeholder="Digite"
        />
      </FormGroup>

      {rule.dim2 ? (
        <FormGroup>
          <Label htmlFor={`${itemPath}.${rule.dim2}`}>Dimensão 2 (mm)</Label>
          <Input
            id={`${itemPath}.${rule.dim2}`}
            autoComplete="off"
            type="number"
            step="any"
            value={(dimValues[rule.dim2] as any) ?? ''}
            onChange={(e) => setValue(`${itemPath}.${rule.dim2}` as any, e.target.value === '' ? undefined : Number(e.target.value), { shouldDirty: true, shouldValidate: false })}
            placeholder="Digite"
          />
        </FormGroup>
      ) : (
        <FormGroup />
      )}

      <FormGroup>
        <Label htmlFor={`${itemPath}.length`}>Comprimento (mm)</Label>
        <Input
          id={`${itemPath}.length`}
          autoComplete="off"
          type="number"
          step="any"
          value={(lengthValue as any) ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            setValue(`${itemPath}.length` as any, raw === '' ? undefined : Number(raw), { shouldDirty: true, shouldValidate: false });
          }}
          placeholder="Digite"
        />
      </FormGroup>

      <FormGroup>
        <Label>Massa calculada (kg)</Label>
        <CalculatedValue>
          {displayMass !== null
            ? displayMass.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
            : '—'}
        </CalculatedValue>
        {displayMass !== null && activeDensity && (
          <CalcHint>por peça · {formatType} · densidade {activeDensity}</CalcHint>
        )}
        {displayMass === null && !activeDensity && (
          <CalcHint>Sem densidade — informe o roteiro ou a matéria-prima do item.</CalcHint>
        )}
        {displayMass === null && !!activeDensity && (
          <CalcHint>Preencha as dimensões e o comprimento (mm).</CalcHint>
        )}
      </FormGroup>

      {materials.length > 1 && (
        <FullRowFormGroup>
          <Label htmlFor={`${itemPath}.referenceMaterial`}>Material de referência (densidade)</Label>
          <Select
            id={`${itemPath}.referenceMaterial`}
            value={selectedMaterialId ?? ''}
            onChange={(e) => setSelectedMaterialId(e.target.value ? Number(e.target.value) : undefined)}
          >
            {materials.map(m => (
              <option key={m.materialId} value={m.materialId}>{m.materialName} ({m.density})</option>
            ))}
          </Select>
          <CalcHint>Usado no cálculo Comprimento ↔ Peso.</CalcHint>
        </FullRowFormGroup>
      )}
    </DimensionsGrid>
  );
};

export default FinalProductDimensionsSection;
